import { addressBookService } from 'ggx-src/address-book/api/api.service'
import { placesService } from 'ggx-service/api/places.service'
import { apiService } from 'ggx-src/delivery/api/api.service'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import _memoize from 'lodash/memoize'

export const FIELD_NAME = {
  NAME: 'name',
  PHONE_NUMBER: 'phone_number',
  ADDRESS: 'address',
  ADDRESS_DETAIL: 'address_detail',
}

const isIncludesWithI18n = (source = '', searchText = '') => {
  const lcSearchText = searchText.toLowerCase()
  return (source || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .includes(lcSearchText)
}

const getContacts = _memoize((field, value) =>
  apiService.getContacts({
    name: field === FIELD_NAME.NAME ? value : '',
  })
)
setInterval(() => getContacts.cache.clear(), 300)
export const buildRecentAddressOptions = async (
  field = FIELD_NAME.ADDRESS,
  value = ''
) => {
  const contacts = (await getContacts(field, value))?.contacts ?? []
  const allOptions = contacts.map(contact => ({
    groupType: 'recent-address',
    label: contact.name,
    value: {
      address: contact.address,
      contact_name: contact.name,
      contact_phone_number: contact.phone_number,
      detail_address: contact.detail !== contact.address ? contact.detail : '',
      latitude: contact.latitude,
      longitude: contact.longitude,
    },
  }))
  const filteredOptions = (() => {
    switch (field) {
      case FIELD_NAME.ADDRESS:
        return allOptions.filter(option =>
          isIncludesWithI18n(option.value.address, value)
        )
      case FIELD_NAME.ADDRESS_DETAIL:
        return allOptions.filter(option =>
          isIncludesWithI18n(option.value.detail_address, value)
        )
      case FIELD_NAME.PHONE_NUMBER:
        return allOptions.filter(option =>
          isIncludesWithI18n(option.value.contact_phone_number, value)
        )
      default:
        return allOptions
    }
  })()
  return filteredOptions.slice(0, 5)
}

export const buildSearchedAddressOptions = async searchText =>
  (await placesService.placesAutocomplete(searchText)).map(location => ({
    value: location,
    label: location.description,
    groupType: 'location',
  }))

const getAddressBookList = _memoize(() => addressBookService.getList())
setInterval(() => getAddressBookList.cache.clear(), 300)
export const buildAddressBookOptions = t => async (
  field = FIELD_NAME.ADDRESS,
  value = ''
) => {
  const allOptions = (await getAddressBookList())
    .sort((a, b) => {
      if (a.last_used_at && b.last_used_at) {
        return (
          new Date(b.last_used_at || 0).getTime() -
          new Date(a.last_used_at || 0).getTime()
        )
      } else if (a.last_used_at) {
        return -1
      } else if (b.last_used_at) {
        return 1
      } else {
        return (
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime()
        )
      }
    })
    .map(item => {
      const matches = item.geo_location
        .substring(7, item.geo_location.length - 1)
        .split(' ')
      return {
        value: {
          address: item.street_address,
          contact_name: item.contact_name,
          contact_phone_number: item.phone_number,
          detail_address: item.address_detail,
          details_address: item.address_detail,
          latitude: Number(matches[1]),
          longitude: Number(matches[0]),
          name: item.nickname,
          saved_address_id: item.id,
        },
        groupType: 'saved-address-book',
      }
    })
  const filteredOptions = (() => {
    switch (field) {
      case FIELD_NAME.ADDRESS:
        return allOptions.filter(option =>
          isIncludesWithI18n(option.value.address, value)
        )
      case FIELD_NAME.ADDRESS_DETAIL:
        return allOptions.filter(option =>
          isIncludesWithI18n(option.value.detail_address, value)
        )
      case FIELD_NAME.NAME:
        return allOptions.filter(option =>
          isIncludesWithI18n(option.value.contact_name, value)
        )
      case FIELD_NAME.PHONE_NUMBER:
        return allOptions.filter(option =>
          isIncludesWithI18n(option.value.contact_phone_number, value)
        )
      default:
        return allOptions
    }
  })()
  const addressBookOptions = filteredOptions.slice(0, 5)
  if (!value && addressBookOptions.length === 0) {
    return [
      {
        groupType: 'save-address-book',
        label: t('save_an_address_to_address_book'),
      },
    ]
  } else if (!value && addressBookOptions.length > 0) {
    return addressBookOptions.concat({
      groupType: 'pick-from-address-book',
      label: t('pick_from_address_book'),
    })
  } else if (
    (field === FIELD_NAME.PHONE_NUMBER || field === FIELD_NAME.NAME) &&
    addressBookOptions.length > 0
  ) {
    return addressBookOptions.concat({
      groupType: 'pick-from-address-book',
      label: t('pick_from_address_book'),
    })
  } else return addressBookOptions
}

export const useDefaultOptions = (
  field = FIELD_NAME.ADDRESS,
  value = '',
  isAddressBookOptionsReady
) => {
  const [t] = useTranslation()
  const [defaultOptions, setDefaultOptions] = useState([])
  useEffect(() => {
    if (isAddressBookOptionsReady)
      Promise.all([
        buildAddressBookOptions(t)(field, ''),
        buildRecentAddressOptions(field, ''),
      ]).then(([addressBookOptions, recentOptions]) =>
        setDefaultOptions([...addressBookOptions, ...recentOptions])
      )
  }, [t, isAddressBookOptionsReady, value])
  return defaultOptions
}

export const useDefaultOptionsWithoutRecentAddress = (
  field = FIELD_NAME.ADDRESS,
  value = '',
  isAddressBookOptionsReady
) => {
  const [t] = useTranslation()
  const [defaultOptions, setDefaultOptions] = useState([])
  useEffect(() => {
    if (isAddressBookOptionsReady)
      buildAddressBookOptions(t)(field, value).then(setDefaultOptions)
  }, [t, isAddressBookOptionsReady, value])
  return defaultOptions
}
